import { useMemo } from 'react';
import { useGetBodyAndPaintData } from './api/useGetBodyAndPaintData';
import {
  DamageMapItemType,
  InsuranceEntryItemType,
  RepairDetailDataType,
} from '@/types/bodyAndPaint';

interface RepairDetailDataHookProps {
  affiliateID: number;
  customerID: number;
  reservationID: number;
  stockCarID: number;
  unitID: number;
}
export const useRepairDetailData = ({
  affiliateID,
  customerID,
  reservationID,
  stockCarID,
  unitID,
}: RepairDetailDataHookProps) => {
  const { bpDetailData, onReload } = useGetBodyAndPaintData({
    reservationID,
    affiliateID,
  });

  const repairDetailData = useMemo(() => {
    const damageMap: Record<keyof DamageMapItemType, string> = {
      front_side_left: bpDetailData?.reservation?.front_side_left
        ? '前方部分 左側の破損'
        : '',
      front_side_center: bpDetailData?.reservation?.front_side_center
        ? '前方部分 中央の破損'
        : '',
      front_side_right: bpDetailData?.reservation?.front_side_right
        ? '前方部分 右側の破損'
        : '',
      front_door_left: bpDetailData?.reservation?.front_door_left
        ? '前ドア部分 左ドアの破損'
        : '',
      front_door_right: bpDetailData?.reservation?.front_door_right
        ? '前ドア部分 右ドアの破損'
        : '',
      rear_door_left: bpDetailData?.reservation?.rear_door_left
        ? '後ドア部分 左ドアの破損'
        : '',
      rear_door_right: bpDetailData?.reservation?.rear_door_right
        ? '後ドア部分 右ドアの破損'
        : '',
      rear_side_left: bpDetailData?.reservation?.rear_side_left
        ? '後方部分 左側の破損'
        : '',
      rear_side_center: bpDetailData?.reservation?.rear_side_center
        ? '後方部分 中央の破損'
        : '',
      rear_side_right: bpDetailData?.reservation?.rear_side_right
        ? '後方部分 右側の破損'
        : '',
    };
    const damagePartText = Object.values(damageMap)
      .filter((item) => item !== '')
      .join('、');
    const factoryAddress = [
      bpDetailData?.plant_info?.address1 ?? '',
      bpDetailData?.plant_info?.address2 ?? '',
      bpDetailData?.plant_info?.address3 ?? '',
    ].join('');
    const openingHours = [
      `${bpDetailData?.plant_info?.opening_time ?? ''} - ${
        bpDetailData?.plant_info?.closing_time ?? ''
      }`,
      [
        !!bpDetailData?.plant_info?.sun ? '日曜日' : undefined,
        !!bpDetailData?.plant_info?.mon ? '月曜日' : undefined,
        !!bpDetailData?.plant_info?.tue ? '火曜日' : undefined,
        !!bpDetailData?.plant_info?.wed ? '水曜日' : undefined,
        !!bpDetailData?.plant_info?.thu ? '木曜日' : undefined,
        !!bpDetailData?.plant_info?.fri ? '金曜日' : undefined,
        !!bpDetailData?.plant_info?.sat ? '土曜日' : undefined,
      ]
        .filter((item) => typeof item === 'string')
        .join('、'),
    ] as const;
    const paymentMethods = Array.isArray(
      bpDetailData?.plant_info?.affiliate_payment_methods,
    )
      ? bpDetailData?.plant_info?.affiliate_payment_methods
          .map((item) => item.payment_method_name)
          .join('、')
      : '';
    const data: RepairDetailDataType = {
      ids: {
        affiliateID: affiliateID,
        bodyPaintID: bpDetailData?.reservation?.t_body_paint_id ?? 0,
        customerID: customerID,
        inspectionID: bpDetailData?.reservation?.t_pm_car_inspection_id ?? 0,
        plantID: bpDetailData?.plant_info?.m_plant_id ?? 0,
        reservationID: reservationID,
        saleID: bpDetailData?.reservation?.t_sale_id ?? 0,
        stockCarID: stockCarID,
        unitID: unitID,
      },
      state_info: {
        status: bpDetailData?.reservation?.status ?? -1,
        check_in_date: new Date(
          bpDetailData?.reservation?.arrival_schedule_datetime ?? '',
        ),
        pick_up_date: new Date(
          bpDetailData?.reservation?.delivery_schedule_datetime ?? '',
        ),
        factory_name: bpDetailData?.plant_info?.plant_name ?? '',
        factory_gps_info: factoryAddress,
        factory_telephone: bpDetailData?.plant_info?.phone_no ?? '',
      },
      reserve_info: {
        car_info: {
          registry_no: bpDetailData?.reservation?.car_info?.registry_no ?? '',
          maker_name: bpDetailData?.reservation?.car_info?.maker_name ?? '',
          car_name: bpDetailData?.reservation?.car_info?.car_name ?? '',
          vin_number: bpDetailData?.reservation?.car_info?.vin ?? '',
        },
        category: '傷・凹み修理',
        factory_name: bpDetailData?.plant_info?.plant_name ?? '',
        check_in_date: new Date(
          bpDetailData?.reservation?.arrival_schedule_datetime ?? '',
        ),
        damage_part: damagePartText,
        notice_date: new Date(
          bpDetailData?.reservation?.damage_location_date ?? '',
        ),
        description: bpDetailData?.reservation?.damage_detail ?? '',
      },
      // TODO;保険情報非存在時に空リストが返ってくるため → nullではないのか？
      // TODO;API改修時に修正
      insurance_info:
        bpDetailData?.insurance_info !== undefined &&
        bpDetailData?.insurance_info !== null &&
        !Array.isArray(bpDetailData?.insurance_info)
          ? {
              company_name: bpDetailData?.insurance_info?.company_name ?? '',
              certificate_number:
                bpDetailData?.insurance_info?.policy_number ?? '',
              receipt_number:
                bpDetailData?.insurance_info?.accident_reception_number ?? '',
              has_deductible: bpDetailData?.insurance_info?.deductible_fee,
              repair_plan: bpDetailData?.insurance_info?.repair_planning ?? 0,
            }
          : undefined,

      factory_info: {
        factory_name: bpDetailData?.plant_info?.plant_name ?? '',
        factory_address: factoryAddress ?? '',
        factory_gps_address: factoryAddress ?? '',
        factory_telephone: bpDetailData?.plant_info?.phone_no ?? '',
        opening_hours: openingHours,
        payment_method: paymentMethods,
      },
    };
    return data;
  }, [
    bpDetailData,
    affiliateID,
    customerID,
    reservationID,
    stockCarID,
    unitID,
  ]);

  const initialInsuranceState = useMemo(() => {
    const {
      company_name,
      policy_number,
      deductible_fee,
      repair_planning,
      accident_reception_number,
    } = bpDetailData?.insurance_info ?? {};
    const valid = 1;
    const invalid = 0;
    const initial = {
      companyPick: company_name ?? '',
      certificateText: policy_number ?? '',
      receiptNumberText: accident_reception_number ?? '',
      deductibleSelect: deductible_fee ? valid : invalid,
      deductibleText: deductible_fee ? `${deductible_fee}` : '',
      repairPlanSelect: repair_planning ?? 0,
    } as InsuranceEntryItemType;
    return initial;
  }, [bpDetailData?.insurance_info]);

  return { repairDetailData, initialInsuranceState, onReload };
};
